const DEFAULT_THEME = {
  accentColor: "#0f766e",
  darkColor: "#0f172a",
  lightColor: "#ffffff",
  textColor: "#334155",
};

const normalizeHex = (value) => {
  const hex = String(value || "").trim().replace("#", "");
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    return `#${hex
      .split("")
      .map((character) => character + character)
      .join("")}`.toLowerCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(hex)) return `#${hex}`.toLowerCase();
  return DEFAULT_THEME.accentColor;
};

const hexToRgb = (hexValue) => {
  const hex = normalizeHex(hexValue).slice(1);
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
};

const mix = (hexValue, target, amount) => {
  const source = hexToRgb(hexValue);
  const ratio = Math.min(1, Math.max(0, amount));
  const channel = (key) =>
    Math.round(source[key] + (target[key] - source[key]) * ratio)
      .toString(16)
      .padStart(2, "0");

  return `#${channel("r")}${channel("g")}${channel("b")}`;
};

const getContrastColor = ({ r, g, b }) => {
  const toLinear = (channel) => {
    const value = channel / 255;
    return value <= 0.03928
      ? value / 12.92
      : ((value + 0.055) / 1.055) ** 2.4;
  };

  const luminance =
    0.2126 * toLinear(r) +
    0.7152 * toLinear(g) +
    0.0722 * toLinear(b);

  return luminance > 0.48 ? "#0f172a" : "#ffffff";
};

export const getThemePalette = (accentColor) => {
  const accent = normalizeHex(accentColor);
  const rgb = hexToRgb(accent);
  const { r, g, b } = rgb;

  return {
    accent,
    accentRgb: `${r} ${g} ${b}`,
    accentContrast: getContrastColor(rgb),
    accentDark: mix(accent, { r: 0, g: 0, b: 0 }, 0.18),
    accentDarker: mix(accent, { r: 0, g: 0, b: 0 }, 0.32),
    accentLight: mix(accent, { r: 255, g: 255, b: 255 }, 0.9),
    accentLighter: mix(accent, { r: 255, g: 255, b: 255 }, 0.96),
    accentBorder: mix(accent, { r: 255, g: 255, b: 255 }, 0.73),
  };
};

export const applyTheme = (colors = DEFAULT_THEME) => {
  if (typeof document === "undefined") return;

  const merged = {
    ...DEFAULT_THEME,
    // Only the accent is user-configurable. Keep the CRM canvas permanently white.
    accentColor: normalizeHex(colors?.accentColor || DEFAULT_THEME.accentColor),
  };
  const palette = getThemePalette(merged.accentColor);
  const root = document.documentElement;

  Object.entries(merged).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value);
  });

  root.style.setProperty("--accentColorDark", palette.accentDark);
  root.style.setProperty("--accentColorDarker", palette.accentDarker);
  root.style.setProperty("--accentColorLight", palette.accentLight);
  root.style.setProperty("--accentColorLighter", palette.accentLighter);
  root.style.setProperty("--accentColorBorder", palette.accentBorder);
  root.style.setProperty("--accentRgb", palette.accentRgb);
  root.style.setProperty("--accentContrast", palette.accentContrast);
  root.dataset.accent = merged.accentColor;

  const themeMeta = document.querySelector?.('meta[name="theme-color"]');
  if (themeMeta) themeMeta.setAttribute("content", merged.accentColor);

  window.dispatchEvent(
    new CustomEvent("crm-theme-change", { detail: { ...merged, ...palette } }),
  );
};

export const loadThemeFromLocalStorage = () => {
  if (typeof window === "undefined") return DEFAULT_THEME;

  try {
    const saved = JSON.parse(localStorage.getItem("themeColors") || "null");
    return {
      ...DEFAULT_THEME,
      accentColor: normalizeHex(saved?.accentColor || DEFAULT_THEME.accentColor),
    };
  } catch {
    return DEFAULT_THEME;
  }
};

export const saveThemeToLocalStorage = (colors) => {
  if (typeof window === "undefined") return;

  const value = {
    ...DEFAULT_THEME,
    accentColor: normalizeHex(colors?.accentColor || DEFAULT_THEME.accentColor),
  };
  try {
    localStorage.setItem(
      "themeColors",
      JSON.stringify({ accentColor: value.accentColor }),
    );
  } catch {
    // Theme preview still works when browser storage is unavailable.
  }
  applyTheme(value);
};

export { DEFAULT_THEME };
